import { useState, useMemo, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Spreadsheet, SheetTabs } from '../../../spreadsheet';
import { useModels } from '../../../../lib/supabase/models/supabaseModelService';
import { useShifts } from '../../../../lib/supabase/shifts/supabaseShiftService';
import { useUsers } from '../../../../lib/supabase/users/supabaseUserService';
import { useExchangeRates } from '../../../../lib/supabase/exchange-rates/supabaseExchangeRateService';
import { useAllPaysForMonth, useCalculateUserMonthlyPay } from '../../../../lib/supabase/pay/supabasePayService';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { getMonthBoundaryBST } from '../../../../lib/utils';
import { AlertTriangle, X, Eye, Download, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../ui/dialog';
import { ScrollArea } from '../../../ui/scroll-area';
import { Badge } from '../../../ui/badge';
import { toast } from 'react-hot-toast';
import {
  calculateUserPayData,
  calculateModelSummaries,
  getSpreadsheetData,
  calculatePayDiscrepancies,
  logPayDiscrepancies,
  type PayCalculationData
} from './modelPaySummaryCalculations';


interface ModelPaySummarySpreadsheetProps {
  defaultMonth?: number;
  defaultYear?: number;
  isFullscreen?: boolean;
}

export function ModelPaySummarySpreadsheet({ defaultMonth, defaultYear, isFullscreen }: ModelPaySummarySpreadsheetProps) {
  const now = DateTime.utc();
  const month_number = defaultMonth !== undefined ? defaultMonth : now.month;
  const year_number = defaultYear !== undefined ? defaultYear : now.year;
  const selected_month = useMemo(() => DateTime.utc(year_number, month_number, 1), [year_number, month_number]);

  const [active_tab, setActiveTab] = useState<string>('');
  const [discrepancies, setDiscrepancies] = useState<PayCalculationData[]>([]);
  const [dismissed_discrepencies, setDismissedDiscrepancies] = useState(false);
  const [discrepency_dialog_open, setDiscrepancyDialogOpen] = useState(false);

  // Fetch data
  const { data: models_data, isLoading: models_loading } = useModels();
  const { data: users_data, isLoading: users_loading } = useUsers();
  const { data: exchange_rates_data, isLoading: exchange_rates_loading } = useExchangeRates();
  const { data: stored_pays = [], isLoading: stored_pays_loading } = useAllPaysForMonth(year_number, month_number);

  // Pay recalculation hook
  const calculatePay = useCalculateUserMonthlyPay();

  // Deep memoization for stable references
  const models = useMemo(() => {
    return models_data || [];
  }, [JSON.stringify(models_data)]);

  const users = useMemo(() => {
    return users_data || [];
  }, [JSON.stringify(users_data)]);

  const exchange_rates = useMemo(() => {
    return exchange_rates_data || [];
  }, [JSON.stringify(exchange_rates_data)]);

  const month_boundary = useMemo(() => getMonthBoundaryBST(selected_month), [selected_month.toMillis()]);

  // Fetch all shifts for the month
  const shifts_filters = useMemo(() => [
    { field: 'start_date_time', operator: 'gte' as const, value: month_boundary.start.toISO() },
    { field: 'start_date_time', operator: 'lte' as const, value: month_boundary.end.toISO() },
    { field: 'submitted', operator: 'eq' as const, value: true }
  ], [month_boundary.start.toString(), month_boundary.end.toString()]);

  const { data: shifts_data = [], isLoading: shifts_loading } = useShifts({
    filters: shifts_filters
  });

  // Get exchange rate for the month
  const exchange_rate = useMemo(() => {
    const rate = exchange_rates?.find(rate =>
      rate.date.month === selected_month.month &&
      rate.date.year === selected_month.year
    );
    return rate?.rate || null;
  }, [exchange_rates, selected_month.month, selected_month.year]);

  // Use TanStack Query for model pay summary calculation
  const { data: pay_data = {}, isLoading: is_calculating } = useQuery({
    queryKey: [
      'model-pay-summary',
      selected_month.month,
      selected_month.year,
      exchange_rate,
      shifts_data.length,
      users.length,
      models.length
    ],
    queryFn: () => calculateUserPayData(shifts_data, users, selected_month, exchange_rate),
    enabled: Boolean(shifts_data.length && users?.length && models?.length),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Calculate model summaries and group by set
  const { data: set_groups = [], isLoading: is_calculating_summaries } = useQuery({
    queryKey: ['model-summaries', pay_data, models.length, selected_month.toMillis()],
    queryFn: () => calculateModelSummaries(pay_data, models, selected_month),
    enabled: Boolean(Object.keys(pay_data).length && models.length),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Initialize active tab when data changes
  if (set_groups.length > 0 && !active_tab) {
    setActiveTab(set_groups[0].label);
  }

  // Get active group data
  const active_group_data = set_groups.find(group => group.label === active_tab);

  // Memoize the processed spreadsheet data
  const processed_spreadsheet_data = useMemo(() => {
    if (!active_group_data) return [];
    return getSpreadsheetData(active_group_data.data);
  }, [active_group_data]);

  // Pre-calculate row indices for performance
  const { total_row_index, gap_row_index } = useMemo(() => {
    const data_length = processed_spreadsheet_data.length;
    return {
      total_row_index: data_length - 1,
      gap_row_index: data_length - 2
    };
  }, [processed_spreadsheet_data]);

  // Calculate loading state
  const is_loading = models_loading || users_loading || exchange_rates_loading || shifts_loading || is_calculating || is_calculating_summaries || stored_pays_loading;

  // Handle pay recalculation for individual users
  const handleRecalculate = async (user_id: string) => {
    try {
      toast.success('Pay recalculation started...');
      await calculatePay.mutateAsync({
        user_id,
        month: month_number,
        year: year_number
      });
      toast.success('Pay recalculated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to recalculate pay');
    }
  };

  // Compare calculated pay with stored pay documents (for sales pay only)
  useEffect(() => {
    const calculated_discrepencies = calculatePayDiscrepancies(pay_data, stored_pays, users);
    setDiscrepancies(calculated_discrepencies);
  }, [pay_data, stored_pays, users, month_number, year_number]);

  if (is_loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading model pay summary...</div>
      </div>
    );
  }

  if (set_groups.length === 0) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
      <div className="text-center py-8 text-muted-foreground">
        No pay data found for {months[month_number - 1]} {year_number}
      </div>
    );
  }

  // Custom column headers
  const column_headers = ['SPLIT %', 'VAT', 'NAME', 'PRE-SPLIT TOTAL', 'SPLIT', 'VAT', 'TOTAL'];

  // Cell styling function
  const getCellStyle = (row_idx: number, col_key: string, value: any) => {
    const is_total_row = row_idx === total_row_index;
    const is_gap_row = row_idx === gap_row_index;

    // Style gap row
    if (is_gap_row) {
      return {
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
      };
    }

    // Style totals row
    if (is_total_row) {
      if (col_key === 'C') { // TOTAL label in NAME column
        return {
          fontWeight: 700,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151'
        };
      } else if (['D', 'E', 'F', 'G'].includes(col_key)) { // Currency columns
        return {
          fontWeight: 700,
          textAlign: 'right' as const,
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151',
          color: '#ef4444'
        };
      } else {
        return {
          backgroundColor: '#f3f4f6',
          borderTop: '2px solid #374151'
        };
      }
    }

    // Style the SPLIT % column (column A)
    if (col_key === 'A' && !is_total_row && !is_gap_row) {
      return {
        textAlign: 'center' as const,
        fontWeight: 500
      };
    }

    // Style the VAT checkbox column (column B)
    if (col_key === 'B' && !is_total_row && !is_gap_row) {
      return {
        textAlign: 'center' as const,
        fontWeight: 500,
        fontSize: '14px'
      };
    }

    // Style the NAME column (column C)
    if (col_key === 'C' && !is_total_row && !is_gap_row) {
      return {
        fontWeight: 500
      };
    }

    // Style currency columns (D, E, F, G)
    if (['D', 'E', 'F', 'G'].includes(col_key) && !is_total_row && !is_gap_row) {
      // Helper function to check if value represents zero
      const isZeroValue = (val: any): boolean => {
        if (typeof val === 'string') {
          const numericValue = val.replace(/[£$,]/g, '');
          return parseFloat(numericValue) === 0;
        }
        return val === 0;
      };

      if (isZeroValue(value)) {
        return {
          color: '#9ca3af',
          fontSize: '12px',
          textAlign: 'right' as const,
          fontWeight: 500
        };
      }

      // Special styling for final TOTAL column (G)
      if (col_key === 'G') {
        return {
          textAlign: 'right' as const,
          fontWeight: 600,
          color: '#ef4444'
        };
      }

      return {
        textAlign: 'right' as const,
        fontWeight: 500
      };
    }

    // Default styling
    return {
      fontSize: '12px'
    };
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col">
            {/* Compact Pay Discrepancy Notification */}
            {discrepancies.length > 0 && !dismissed_discrepencies && (
              <div className="border border-brand-gold/50 bg-brand-dark/10 rounded-xl mb-2 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-brand-gold flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-brand-gold">
                        Sales Pay Calculation Mismatch Detected
                      </h3>
                      <p className="text-xs text-brand-gold/80">
                        {discrepancies.length} user{discrepancies.length !== 1 ? 's have' : ' has'} discrepancies between calculated and stored sales pay
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Dialog open={discrepency_dialog_open} onOpenChange={setDiscrepancyDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-brand-gold dark:text-brand-gold" />
                            Sales Pay Discrepancies - {selected_month.toFormat('MMMM yyyy')}
                          </DialogTitle>
                        </DialogHeader>

                        <ScrollArea className="max-h-[60vh] pr-4">
                          <div className="space-y-4">
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-brand-dark dark:text-foreground border-b border-brand-border dark:border-brand-border pb-2">
                                Affected Users ({discrepancies.length})
                              </h4>

                              {discrepancies.map((discrepancy, index) => (
                                <Card key={index} className="p-4">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h5 className="font-medium text-brand-dark dark:text-foreground">
                                        {discrepancy.user_name}
                                      </h5>
                                      <p className="text-xs text-muted-foreground dark:text-muted-foreground font-mono">
                                        ID: {discrepancy.user_id}
                                      </p>
                                    </div>
                                    {discrepancy.stored_pay_id && (
                                      <div className="text-xs text-muted-foreground dark:text-muted-foreground">
                                        <div className="text-muted-foreground dark:text-muted-foreground">Stored Pay ID:</div>
                                        <div className="font-mono">{discrepancy.stored_pay_id}</div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-3 gap-3 mb-4">
                                    <div className="text-left">
                                      <div className="text-xs text-muted-foreground dark:text-muted-foreground mb-1">
                                        Current Calculation
                                      </div>
                                      <div className="text-lg font-semibold text-brand-dark dark:text-foreground">
                                        £{discrepancy.calculated_total.toFixed(2)}
                                      </div>
                                    </div>
                                    <div className="text-left">
                                      <div className="text-xs text-muted-foreground dark:text-muted-foreground mb-1">
                                        Stored Amount
                                      </div>
                                      <div className="text-lg font-semibold text-foreground dark:text-muted-foreground">
                                        £{discrepancy.stored_total.toFixed(2)}
                                      </div>
                                    </div>
                                    <div className="text-left">
                                      <div className="text-xs text-muted-foreground dark:text-muted-foreground mb-1">
                                        Difference
                                      </div>
                                      <Badge variant="destructive" className="text-sm font-bold">
                                        £{discrepancy.difference.toFixed(2)}
                                      </Badge>
                                    </div>
                                  </div>

                                  <div className="flex justify-end pt-2 border-t border-brand-border dark:border-brand-border">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRecalculate(discrepancy.user_id)}
                                      disabled={calculatePay.isPending}
                                      className="h-8"
                                    >
                                      <RefreshCw className={`w-3 h-3 mr-1 ${calculatePay.isPending ? 'animate-spin' : ''}`} />
                                      {calculatePay.isPending ? 'Recalculating...' : 'Recalculate Pay'}
                                    </Button>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </div>
                        </ScrollArea>

                        <div className="flex justify-between items-center pt-4 border-t border-brand-border dark:border-brand-border">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => logPayDiscrepancies(discrepancies, month_number, year_number)}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export to Console
                          </Button>

                          <Button onClick={() => setDiscrepancyDialogOpen(false)} size="sm">
                            Close
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDismissedDiscrepancies(true)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {active_group_data && (
              <Spreadsheet
                columns={7}
                minRows={Math.max(processed_spreadsheet_data.length, 20)}
                data={processed_spreadsheet_data}
                editable={false}
                columnWidths={[80, 60, 200, 120, 120, 120, 120]}
                rowHeight={35}
                showColumnHeaders={true}
                customColumnHeaders={column_headers}
                fillHeight={true}
                isFullscreen={isFullscreen}
                buffers={{ fullscreen: 65, normal: 65 }}
                getCellStyle={getCellStyle}
                csvFilename={`model-pay-summary-${year_number}-${(month_number + 1).toString().padStart(2, '0')}.csv`}
                csvIncludeHeaders={true}
                className="rounded-b-none"
              />
            )}

            <SheetTabs
              tabs={set_groups.map(group => ({
                id: group.label,
                label: group.label
              }))}
              activeTab={active_tab}
              onTabChange={setActiveTab}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ModelPaySummarySpreadsheet; 